// Per-day domain data + shared hooks.
//
// Centralizes the meal anchors (budget per slot from the effective target),
// the plan/workout derivation for a date, the coach-target hooks, and
// `useDayData(date)` — the single read+mutation bundle every inspector uses.
// The week ledger (`ledger-week.ts`) reads the same query keys, so an
// inspector's optimistic update lands in the grid instantly.

import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { apiClient, ApiError } from './api';
import { formatLocalDateToYMD, parseLocalDate } from './date';
import { STALE_TIME_DAILY, STALE_TIME_SLOW } from './query';
import { workoutTypeToKind, type WeekDay } from './protocol';
import { coachKind } from './coach-plan';
import {
  ActiveRunPlanSchema,
  type ActiveRunPlan,
  ActualSetRowsSchema,
  CoachLatestResponseSchema,
  type CoachLatestResponse,
  DEFAULT_PROFILE,
  DailyRowSchema,
  DailyRowsSchema,
  DailyRunTargetsSchema,
  type DailyRunTarget,
  DailyTargetHistoryListSchema,
  type DailyTargetHistory,
  ExerciseWeightRowSchema,
  ExerciseWeightRowsSchema,
  formatRunSegments,
  ProfileResponseSchema,
  SpeedianceMappingRowsSchema,
  StrengthByMuscleResponseSchema,
  type StrengthByMuscleResponse,
  SurfaceCommentRowSchema,
  SurfaceCommentRowsSchema,
  type SurfaceCommentRow,
  type SurfaceType,
  WeightRowSchema,
  WeightRowsSchema,
  WorkoutRowSchema,
  WorkoutRowsSchema,
  type ActualSetRow,
  type DailyRow,
  type ExerciseWeightRow,
  type ExercisesDoneMap,
  type MealLog,
  type MealsEatenMap,
  type ManualBodyweightActuals,
  ManualBodyweightActualsResponseSchema,
  type ManualBodyweightActualsResponse,
  type ProfileResponse,
  type SpeedianceMappingRow,
  type WeeklyCommitments,
  type WeightRow,
  type WorkoutRow,
  type WorkoutStatus,
} from './types';

// ──────────────────────────────────────────────────────────────────────────
// Meal anchors
// ──────────────────────────────────────────────────────────────────────────

export type MealSlot = 'workout' | 'lunch' | 'snack' | 'dinner';

export type MealAnchor = {
  slot: MealSlot;
  time: string;       // HH:MM
  minutes: number;    // minutes since midnight (derived from time)
  name: string;
  cal: number;
  p: number;
  f: number;
  c: number;
  anchors: string[];  // example meal options
};

function toMinutes(hhmm: string): number {
  const [h, m] = hhmm.split(':').map(Number);
  return (h ?? 0) * 60 + (m ?? 0);
}

export const MEALS: MealAnchor[] = [
  {
    slot: 'workout',
    time: '09:00',
    minutes: toMinutes('09:00'),
    name: 'Workout · Fasted',
    cal: 0, p: 0, f: 0, c: 0,
    anchors: ['Coffee black. No food.', 'Optional: 25g whey + water right after.'],
  },
  {
    slot: 'lunch',
    time: '12:00',
    minutes: toMinutes('12:00'),
    name: 'Lunch · Break fast',
    cal: 850, p: 65, f: 25, c: 85,
    anchors: [
      '200g chicken + 80g rice + veg + olive oil',
      '180g salmon + 250g potato + salad',
      '220g lean beef + grains + greens',
    ],
  },
  {
    slot: 'snack',
    time: '16:30',
    minutes: toMinutes('16:30'),
    name: 'Snack',
    cal: 400, p: 45, f: 10, c: 35,
    anchors: [
      '200g Greek yogurt 0% + 1 scoop whey + banana',
      '200g cottage cheese + apple + 15g almonds',
      '2 eggs + whey shake + 2 rice cakes',
    ],
  },
  {
    slot: 'dinner',
    time: '19:30',
    minutes: toMinutes('19:30'),
    name: 'Dinner',
    cal: 1050, p: 90, f: 35, c: 100,
    anchors: [
      '250g chicken + 100g rice + veg + 150g skyr',
      '200g salmon + 150g lentils + cottage cheese',
      '220g lean beef + 250g sweet potato + Greek yogurt',
    ],
  },
];

export type MacroTarget = {
  goal_cal: number;
  goal_protein_g: number;
  goal_fat_g: number;
  goal_carb_g: number;
};

export type MealPlanValidation = {
  valid: boolean;
  totals: MealLog;
  issues: string[];
};

// Preserve the familiar lunch/snack/dinner cadence, but size each macro budget
// from the target effective on the viewed date. The proportions are based on
// the original anchors; the final meal receives the rounding remainder so the
// three rows always add up to the target exactly.
const MEAL_MACRO_SHARES = {
  lunch: { p: 0.325, f: 0.357, c: 0.386 },
  snack: { p: 0.225, f: 0.143, c: 0.159 },
} as const;

function splitTarget(total: number, firstShare: number, secondShare: number): [number, number, number] {
  const first = Math.max(0, Math.round(total * firstShare));
  const second = Math.max(0, Math.round(total * secondShare));
  return [first, second, Math.max(0, total - first - second)];
}

/**
 * Builds the plan shown on Day. This is a macro allocation, not a meal log:
 * the operator still records only actual kcal/protein/fat/carbs.
 */
export function buildMealPlan(target: MacroTarget): MealAnchor[] {
  const [lunchP, snackP, dinnerP] = splitTarget(
    target.goal_protein_g,
    MEAL_MACRO_SHARES.lunch.p,
    MEAL_MACRO_SHARES.snack.p,
  );
  const [lunchF, snackF, dinnerF] = splitTarget(
    target.goal_fat_g,
    MEAL_MACRO_SHARES.lunch.f,
    MEAL_MACRO_SHARES.snack.f,
  );
  const [lunchC, snackC, dinnerC] = splitTarget(
    target.goal_carb_g,
    MEAL_MACRO_SHARES.lunch.c,
    MEAL_MACRO_SHARES.snack.c,
  );

  const macroCalories = (p: number, f: number, c: number) => p * 4 + f * 9 + c * 4;
  const lunchEnergy = macroCalories(lunchP, lunchF, lunchC);
  const snackEnergy = macroCalories(snackP, snackF, snackC);
  const dinnerEnergy = macroCalories(dinnerP, dinnerF, dinnerC);
  const macroEnergyTotal = lunchEnergy + snackEnergy + dinnerEnergy;
  const calorieScale = macroEnergyTotal > 0 ? target.goal_cal / macroEnergyTotal : 0;
  const lunchCal = Math.max(0, Math.round(lunchEnergy * calorieScale));
  const snackCal = Math.max(0, Math.round(snackEnergy * calorieScale));
  const dinnerCal = Math.max(0, target.goal_cal - lunchCal - snackCal);

  const allocated: Record<Exclude<MealSlot, 'workout'>, MealLog> = {
    lunch: { cal: lunchCal, p: lunchP, f: lunchF, c: lunchC },
    snack: { cal: snackCal, p: snackP, f: snackF, c: snackC },
    dinner: { cal: dinnerCal, p: dinnerP, f: dinnerF, c: dinnerC },
  };
  return MEALS.map((meal) =>
    meal.slot === 'workout' ? meal : { ...meal, ...allocated[meal.slot] },
  );
}

export function validateMealPlan(plan: MealAnchor[], target: MacroTarget): MealPlanValidation {
  const food = plan.filter((m) => m.slot !== 'workout');
  const totals = food.reduce<MealLog>(
    (sum, m) => ({ cal: sum.cal + m.cal, p: sum.p + m.p, f: sum.f + m.f, c: sum.c + m.c }),
    { cal: 0, p: 0, f: 0, c: 0 },
  );
  const issues: string[] = [];
  if (food.length !== 3 || new Set(food.map((m) => m.slot)).size !== 3) {
    issues.push('The plan must contain lunch, snack, and dinner exactly once.');
  }
  if (totals.cal !== target.goal_cal) issues.push('Meal calories do not match the daily target.');
  if (totals.p !== target.goal_protein_g) issues.push('Meal protein does not match the daily target.');
  if (totals.f !== target.goal_fat_g) issues.push('Meal fat does not match the daily target.');
  if (totals.c !== target.goal_carb_g) issues.push('Meal carbs do not match the daily target.');
  const macroEnergy = totals.p * 4 + totals.f * 9 + totals.c * 4;
  if (totals.cal > 0 && Math.abs(macroEnergy - totals.cal) / totals.cal > 0.1) {
    issues.push('Calories and macro-derived energy differ by more than 10%.');
  }
  return { valid: issues.length === 0, totals, issues };
}

// ──────────────────────────────────────────────────────────────────────────
// Day name + ISO helpers (mirrors today.tsx)
// ──────────────────────────────────────────────────────────────────────────

const DAY_NAMES = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'] as const;
type DayName = typeof DAY_NAMES[number];

export const isoToday = (): string => formatLocalDateToYMD(new Date());

export function dayName(iso: string): DayName {
  const [y, m, dd] = iso.split('-').map(Number);
  return DAY_NAMES[new Date(y!, m! - 1, dd!).getDay()]!;
}

// Meal-eaten state lives on the daily_log row itself (column `meals_eaten`,
// added in migration 0005). The Day route reads it from the row alongside
// kcal / protein / fat / carb and persists it via the same /api/daily POST.
// There's no per-device localStorage shadow — the row IS the truth, and the
// optimistic mutation makes the chip feel instant.
//
// `meals_eaten[slot]` carries the actual macros the operator logged (so the
// +EATEN chip can be expanded into editable inputs that override the anchor
// defaults). For back-compat we also accept boolean `true` (legacy "use
// anchor defaults") and fall through to MEALS[slot]'s values when resolving.

/**
 * Resolve a per-slot meals_eaten value to a concrete MealLog or null. Used
 * both for displaying current values and for computing deltas on update.
 */
export function resolveMealLog(
  slot: MealSlot,
  value: MealsEatenMap[keyof MealsEatenMap] | undefined,
  _plan: MealAnchor[] = MEALS,
): MealLog | null {
  if (value == null || value === false) return null;
  if (value === true) {
    // Legacy booleans were written when the static protocol anchors were the
    // only defaults. Resolve them against that historical snapshot—not the
    // current target-derived plan—or an edit/unlog can subtract macros that
    // were never added to the day total.
    const m = MEALS.find((x) => x.slot === slot);
    if (!m) return null;
    return { cal: m.cal, p: m.p, f: m.f, c: m.c };
  }
  return value;
}

// ──────────────────────────────────────────────────────────────────────────
// Phase + weekly rate derivation
// ──────────────────────────────────────────────────────────────────────────

export type Phase = 'CUT' | 'RECOMP' | 'BULK';

export function derivePhase(weeklyRate: number, explicit?: string | null): Phase {
  if (explicit === 'CUT' || explicit === 'RECOMP' || explicit === 'BULK') return explicit;
  if (weeklyRate < -0.1) return 'CUT';
  if (weeklyRate > 0.1) return 'BULK';
  return 'RECOMP';
}

// Mean per-week delta over the last 14 days of the sorted weight log.
// Returns 0 when there aren't enough points to support a rolling trend.
export function computeWeeklyRate(sortedWeights: WeightRow[]): number {
  if (sortedWeights.length < 2) return 0;
  const last = sortedWeights[sortedWeights.length - 1]!;
  // Parse as local-midnight so date arithmetic doesn't shift in negative-UTC
  // zones (new Date('YYYY-MM-DD') is UTC).
  const cutoff = parseLocalDate(last.date);
  cutoff.setDate(cutoff.getDate() - 14);
  const since = formatLocalDateToYMD(cutoff);
  const window = sortedWeights.filter((w) => w.date >= since);
  if (window.length < 2) return 0;
  const first = window[0]!;
  const lastW = window[window.length - 1]!;
  const days =
    (parseLocalDate(lastW.date).getTime() - parseLocalDate(first.date).getTime()) /
    (24 * 60 * 60 * 1000);
  if (days <= 0) return 0;
  return ((lastW.weight_kg - first.weight_kg) / days) * 7;
}

// True weeks elapsed since profile.start_date (1-indexed). Returns 1 when the
// start date is missing or in the future ("preparing to start next Monday").
//
// NOT clamped to RUN_PLAN.length: coach run arcs can exceed 12 weeks (e.g. a
// 16-week half-marathon build), and the arc highlight matches the true elapsed
// week against the coach arc's week_index. Every consumer that INDEXES the
// static 12-week RUN_PLAN re-clamps locally (Math.min(RUN_PLAN.length-1, …)),
// so the raw value is safe to hand out. Display labels show the true week.
export function programWeek(profile: ProfileResponse): number {
  if (!profile.start_date) return 1;
  const start = parseLocalDate(profile.start_date).getTime();
  if (!Number.isFinite(start)) return 1;
  const weeks = Math.floor((Date.now() - start) / (7 * 24 * 60 * 60 * 1000)) + 1;
  return Math.max(1, weeks);
}

// Soft filter: hide entries dated before the program's start_date. Used to
// scope all date-keyed reads (weights, daily log, workout log, actual sets)
// to "this program" without deleting historical rows. Future start_date
// hides everything, which is the intended behavior for the "preparing to
// start next Monday" flow.
export function filterFromStart<T extends { date: string }>(
  items: T[],
  startDate: string | null | undefined,
): T[] {
  if (!startDate) return items;
  return items.filter((r) => r.date >= startDate);
}

// ──────────────────────────────────────────────────────────────────────────
// Coach-applied run targets (daily_run_target) — overlay over RUN_PLAN
// ──────────────────────────────────────────────────────────────────────────

// Fetch all coach-applied run targets (one window covering the recent past +
// near future is enough for the Day/Plan surfaces). The run surfaces prefer a
// matching date's target over the static RUN_PLAN.
export function useDailyRunTargets(): Map<string, DailyRunTarget> {
  const q = useQuery<DailyRunTarget[]>({
    queryKey: ['daily-run-target', 'list'],
    queryFn: () =>
      apiClient.get<DailyRunTarget[]>('/api/daily-run-target/list', {
        schema: DailyRunTargetsSchema,
      }),
    staleTime: STALE_TIME_SLOW,
  });
  const byDate = new Map<string, DailyRunTarget>();
  for (const t of q.data ?? []) byDate.set(t.date, t);
  return byDate;
}

// Speediance exercise mapping (group_id → variant / unilateral). Shared so the
// Day session surfaces can resolve a coach exercise's unilateral flag from the
// mapping (the coach's free-form name often omits the "/side" cue). Same
// query key + staleTime as the Settings mapping manager.
export function useSpeedianceMapping(): SpeedianceMappingRow[] {
  const q = useQuery<SpeedianceMappingRow[]>({
    queryKey: ['speediance', 'mapping'],
    queryFn: () =>
      apiClient.get<SpeedianceMappingRow[]>('/api/speediance/mapping', {
        schema: SpeedianceMappingRowsSchema,
      }),
    staleTime: STALE_TIME_SLOW,
  });
  return q.data ?? [];
}

// Operator's subjective comments for a given DATE (all surfaces). One query per
// active date; each Day surface filters client-side by its `surface_type`. The
// add-mutation invalidates the date's query on settle. Comments are append-only
// history that feeds the coach — adding one does NOT trigger a coach call.
export function useSurfaceComments(date: string): {
  comments: SurfaceCommentRow[];
  add: (surfaceType: SurfaceType, text: string) => Promise<SurfaceCommentRow>;
  isAdding: boolean;
} {
  const qc = useQueryClient();
  const q = useQuery<SurfaceCommentRow[]>({
    queryKey: ['surface-comments', date],
    queryFn: () =>
      apiClient.get<SurfaceCommentRow[]>(
        `/api/surface-comments?from=${date}&to=${date}`,
        { schema: SurfaceCommentRowsSchema },
      ),
    staleTime: STALE_TIME_SLOW,
  });
  const mut = useMutation<SurfaceCommentRow, Error, { surfaceType: SurfaceType; text: string }>({
    mutationFn: ({ surfaceType, text }) =>
      apiClient.post<SurfaceCommentRow>(
        '/api/surface-comments',
        { date, surface_type: surfaceType, text },
        { schema: SurfaceCommentRowSchema },
      ),
    onSettled: () => qc.invalidateQueries({ queryKey: ['surface-comments', date] }),
  });
  return {
    comments: q.data ?? [],
    // mutateAsync so callers can await the POST and only clear their draft on
    // success — a failed write must not silently drop the operator's text.
    add: (surfaceType, text) => mut.mutateAsync({ surfaceType, text }),
    isAdding: mut.isPending,
  };
}

// Latest coach call (no coach_call_id → server returns the newest ok row).
// Used by the Day-view run arc to render the coach's plan_run_arc when a call
// exists. retry:false + a null fallback so a 404 (no coach call yet) doesn't
// surface as an error — the consumer just falls back to the static RUN_PLAN.
export function useCoachLatest() {
  return useQuery<CoachLatestResponse | null>({
    queryKey: ['coach-latest'],
    queryFn: async () => {
      try {
        return await apiClient.get<CoachLatestResponse>('/api/coach/latest', {
          schema: CoachLatestResponseSchema,
        });
      } catch (err) {
        if (err instanceof ApiError && err.kind === 'not_found') return null;
        throw err;
      }
    },
    retry: false,
    staleTime: STALE_TIME_SLOW,
  });
}

// The ACCEPTED run plan in effect on `date` (accept-gated + as-of) — the arc of
// the most recently APPLY-ALL'd coach call whose plan started on or before the
// date, or null when nothing is accepted yet. The Day-view arc reads THIS, not
// the raw latest proposal — so an unaccepted coach run never shows, and a plan
// that starts tomorrow shows the previous one today (same model as macros).
export function useActiveRunPlan(date: string) {
  return useQuery<ActiveRunPlan>({
    queryKey: ['run-plan-active', date],
    queryFn: () =>
      apiClient.get<ActiveRunPlan>(`/api/run-plan/active?date=${date}`, {
        schema: ActiveRunPlanSchema,
      }),
    staleTime: STALE_TIME_SLOW,
  });
}

// The coach-arc week_index that is CURRENT on `today` = the latest week whose
// week_start_date <= today (the arc is date-anchored). When the arc weeks carry
// no week_start_date (pre-anchor coach calls), fall back to `fallbackWeek` (the
// operator's elapsed program week). Returns the first week's index when today
// precedes the whole arc.
export function resolveArcCurrentWeek(
  arc: Array<{ week_index: number; week_start_date?: string }>,
  today: string,
  fallbackWeek: number,
): number {
  if (arc.length === 0) return fallbackWeek;
  // Sort ascending by week_start_date so the break-on-first-future loop is
  // correct even if the arc arrives unordered (ISO dates compare chronologically).
  const dated = arc
    .filter((w) => typeof w.week_start_date === 'string')
    .sort((a, b) => (a.week_start_date as string).localeCompare(b.week_start_date as string));
  if (dated.length === 0) return fallbackWeek;
  let current = dated[0]!.week_index;
  let found = false;
  for (const w of dated) {
    if ((w.week_start_date as string) <= today) {
      current = w.week_index;
      found = true;
    } else break;
  }
  // If today is before the whole arc, highlight the first (earliest) week.
  return found ? current : dated[0]!.week_index;
}

// The CURRENT program week, anchored to the accepted coach run arc when one is
// active (so the goal-track week matches the run-arc card's highlighted bar),
// falling back to the profile's elapsed program week (start_date vs today)
// when no coach arc exists. Returns the arc too so callers can derive this
// week's date window for trend math.
export function useCoachProgramWeek(today: string): {
  week: number;
  arc: Array<{ week_index: number; week_start_date?: string }>;
} {
  const profileQ = useQuery<ProfileResponse>({
    queryKey: ['profile'],
    // Mirror useProgramStartDate's queryFn (same key → shared cache): a 404
    // (no profile yet, first run) falls back to defaults instead of throwing.
    queryFn: async () => {
      try {
        return await apiClient.get<ProfileResponse>('/api/profile', {
          schema: ProfileResponseSchema,
        });
      } catch (err) {
        if (err instanceof ApiError && err.kind === 'not_found') return DEFAULT_PROFILE;
        throw err;
      }
    },
    staleTime: STALE_TIME_SLOW,
  });
  const active = useActiveRunPlan(today);
  const arc = active.data?.plan_run_arc ?? [];
  const fallback = profileQ.data ? programWeek(profileQ.data) : 1;
  const week = arc.length > 0 ? resolveArcCurrentWeek(arc, today, fallback) : fallback;
  return { week, arc };
}

export type { DailyRunTarget } from './types';

// The four macro goals effective on a given date.
export type EffectiveTarget = {
  goal_cal: number;
  goal_protein_g: number;
  goal_fat_g: number;
  goal_carb_g: number;
  /** Provenance of the as-of row used for this date. */
  date: string;
  source: string | null;
  updated_at: number;
};

const DEFAULT_TARGET_MACROS = {
  goal_cal: DEFAULT_PROFILE.goal_cal ?? 2300,
  goal_protein_g: DEFAULT_PROFILE.goal_protein_g ?? 200,
  goal_fat_g: DEFAULT_PROFILE.goal_fat_g ?? 70,
  goal_carb_g: DEFAULT_PROFILE.goal_carb_g ?? 220,
};

// Per-date macro target history (0024), ascending by date. Fetched once; the
// Day/Progress surfaces resolve per-date targets locally via
// resolveEffectiveTarget so a past day shows the target that applied THEN.
export function useDailyTargetHistory(): DailyTargetHistory[] {
  const q = useQuery<DailyTargetHistory[]>({
    queryKey: ['daily-target-history', 'list'],
    queryFn: () =>
      apiClient.get<DailyTargetHistory[]>('/api/daily-target-history/list', {
        schema: DailyTargetHistoryListSchema,
      }),
    staleTime: STALE_TIME_SLOW,
  });
  return [...(q.data ?? [])].sort((a, b) => a.date.localeCompare(b.date));
}

// Per-region strength volume trajectory (Push/Pull/Legs/Core), bucketed into
// program weeks from `startDate`. Server-side join (actual_set → mapping →
// catalog muscle → region) since the catalog lives in D1. `regions` is [] until
// loaded; `isLoading` lets the card distinguish "still fetching" from
// "genuinely empty" (no flash of the empty-state). A 12-week trailing window is
// used when startDate is null (server default).
export function useStrengthByMuscle(startDate: string | null): {
  regions: StrengthByMuscleResponse['regions'];
  bodyweight: NonNullable<StrengthByMuscleResponse['bodyweight']> | null;
  isLoading: boolean;
} {
  const q = useQuery<StrengthByMuscleResponse>({
    queryKey: ['strength-by-muscle', startDate ?? 'default'],
    queryFn: () =>
      apiClient.get<StrengthByMuscleResponse>(
        `/api/strength/by-muscle${startDate ? `?from=${startDate}` : ''}`,
        { schema: StrengthByMuscleResponseSchema },
      ),
    staleTime: STALE_TIME_SLOW,
  });
  return {
    regions: q.data?.regions ?? [],
    bodyweight: q.data?.bodyweight ?? null,
    isLoading: q.isLoading,
  };
}

// The macro target effective ON `date` = the most recent history row with
// date <= date; before the earliest row, DEFAULT_TARGET (CLAUDE.md defaults).
// `history` MUST be sorted ascending (useDailyTargetHistory guarantees it).
export function resolveEffectiveTarget(
  history: DailyTargetHistory[],
  date: string,
): EffectiveTarget {
  let match: DailyTargetHistory | null = null;
  for (const row of history) {
    if (row.date <= date) match = row;
    else break;
  }
  if (!match) return { ...DEFAULT_TARGET_MACROS, date, source: null, updated_at: 0 };
  return {
    goal_cal: match.goal_cal,
    goal_protein_g: match.goal_protein_g,
    goal_fat_g: match.goal_fat_g,
    goal_carb_g: match.goal_carb_g,
    date: match.date,
    source: match.source,
    updated_at: match.updated_at,
  };
}

// ISO date (YYYY-MM-DD) of `weekday` in the Mon-first week containing
// `anchorYmd`. Maps a week-strip weekday tile to its real date so we can look
// up a coach run target for it.
const WEEKDAY_INDEX: Record<WeekDay['day'], number> = {
  Mon: 0, Tue: 1, Wed: 2, Thu: 3, Fri: 4, Sat: 5, Sun: 6,
};
export function dateForWeekday(anchorYmd: string, weekday: WeekDay['day']): string {
  // mondayOf lives in cardio-queries; inline the Mon-first math here to avoid
  // a cross-import cycle (cardio-queries imports from day-data's siblings).
  const [ay, am, ad] = anchorYmd.split('-').map(Number) as [number, number, number];
  const anchor = new Date(Date.UTC(ay, am - 1, ad));
  const dow = (anchor.getUTCDay() + 6) % 7; // 0=Mon
  const mondayTs = anchor.getTime() - dow * 86_400_000;
  const ts = mondayTs + WEEKDAY_INDEX[weekday] * 86_400_000;
  const dt = new Date(ts);
  const pad = (n: number) => String(n).padStart(2, '0');
  return `${dt.getUTCFullYear()}-${pad(dt.getUTCMonth() + 1)}-${pad(dt.getUTCDate())}`;
}

// Format a coach run target as a one-line prescription string, matching the
// shape RUN_PLAN's easy/long fields produce ("45 min @ Z2 · 5:41/km").
export function formatRunTarget(t: DailyRunTarget): string {
  const pace =
    t.target_pace_s_per_km != null
      ? ` · ${Math.floor(t.target_pace_s_per_km / 60)}:${String(t.target_pace_s_per_km % 60).padStart(2, '0')}/km`
      : '';
  // 0028 — when the target carries structured segments, render the structure
  // ("10′ Z2 · 4×3′ Z3 (2′ Z1) · 10′ Z2") instead of the flat "45 min @ Z2".
  if (t.segments && t.segments.length > 0) {
    return `${formatRunSegments(t.segments)}${pace}`;
  }
  return `${t.duration_min} min @ ${t.zone}${pace}`;
}

// ──────────────────────────────────────────────────────────────────────────
// The day-data hook — single source of truth for the Day route + desktop.
// ──────────────────────────────────────────────────────────────────────────

export type WorkoutMutVars = {
  date: string;
  type: string;
  status?: WorkoutStatus;
  exercises_done?: ExercisesDoneMap;
};

export type WorkoutOverrideVars = {
  date: string;
  type: string;
};

// Type → label for a synthesised WeekDay. Every workout type resolves here:
// an operator override to a type off its usual rhythm, a weekday commitment
// with no row yet, or a coach row that lost its prescription.
const PLAN_FALLBACK: Record<WeekDay['type'], string> = {
  Strength: 'Strength',
  'Run-Easy': 'Easy Run',
  'Run-Long': 'Long Run',
  'Run-Tempo': 'Tempo Run',
  'Run-Intervals': 'Intervals',
  'Run-Recovery': 'Recovery Run',
  Padel: 'Padel',
  Rest: 'Rest',
  // Coach-only type: the label only matters when the row lost its
  // prescription (orphaned suggestion) — a prescribed row takes its name
  // from coach_prescription in deriveTodayPlanAndWorkout.
  Mixed: 'Mixed session',
};


/**
 * Derive `todayPlan` + `todayWorkout` for a given date. Priority:
 *   1. Any non-skipped `workout_log` row for the date wins — its `type`
 *      drives the active plan; the label is the coach's prescription name
 *      when there is one, else PLAN_FALLBACK's label for the type.
 *   2. Otherwise the weekday's standing commitment, if the operator armed
 *      one. `todayWorkout` is undefined either way.
 *   3. No row and no commitment — the day has no plan at all. Callers
 *      render their unplanned treatment; nothing is invented.
 *
 * Exported for unit-level coverage if the test suite grows.
 */
export function deriveTodayPlanAndWorkout(
  workouts: WorkoutRow[],
  date: string,
  commitments: WeeklyCommitments | null,
): { todayPlan: WeekDay | undefined; todayWorkout: WorkoutRow | undefined } {
  const day = dayName(date);
  // Single non-skipped row per date is the invariant the override write
  // path maintains. If multiple ever leak through (e.g. legacy data), the
  // most-recently-updated one wins — best approximation of "the operator's
  // current intent".
  const candidates = workouts
    .filter((r) => r.date === date && r.status !== 'skipped')
    .sort((a, b) => b.updated_at - a.updated_at);
  const active = candidates[0];

  if (active) {
    const type = active.type as WeekDay['type'];
    // Plan 4 U6 — coach > manual priority. A coach-sourced row carries a
    // coach_prescription inlined from the server's LEFT JOIN. Render the
    // LLM's free-form name as the plan label and pick an icon by category
    // (matches REVIEW's WeekByWeekPlan glyphs); the concrete type passes
    // through so mutations land on the row's (date, type) key. 'rest' kind
    // from the prescription gets the open-circle.
    const p = active.coach_prescription;
    const todayPlan: WeekDay = p
      ? { day, plan: p.name, kind: coachKind(p.kind, p.category, p.name), type }
      : { day, plan: PLAN_FALLBACK[type] ?? active.type, kind: workoutTypeToKind(type), type };
    return { todayPlan, todayWorkout: active };
  }

  const committed = commitments?.[day] ?? null;
  if (!committed) return { todayPlan: undefined, todayWorkout: undefined };
  return {
    todayPlan: {
      day,
      plan: PLAN_FALLBACK[committed],
      kind: workoutTypeToKind(committed),
      type: committed,
    },
    todayWorkout: undefined,
  };
}

// The operator profile (404 → CLAUDE.md defaults). One query key shared by
// every surface; TanStack dedupes.
export function useProfile() {
  return useQuery<ProfileResponse>({
    queryKey: ['profile'],
    queryFn: async () => {
      try {
        return await apiClient.get<ProfileResponse>('/api/profile', {
          schema: ProfileResponseSchema,
        });
      } catch (err) {
        if (err instanceof ApiError && err.kind === 'not_found') return DEFAULT_PROFILE;
        throw err;
      }
    },
    staleTime: STALE_TIME_SLOW,
  });
}

export function useDayData(date: string) {
  const qc = useQueryClient();

  const profileQuery = useProfile();

  // Per-date macro target history — resolved to the active `date` below so the
  // Day surface (incl. archive scrub) scores against the target in force then.
  const targetHistory = useDailyTargetHistory();

  const weightsQuery = useQuery<WeightRow[]>({
    queryKey: ['weights'],
    queryFn: () => apiClient.get<WeightRow[]>('/api/weights', { schema: WeightRowsSchema }),
    staleTime: STALE_TIME_DAILY,
  });

  const dailyQuery = useQuery<DailyRow[]>({
    queryKey: ['daily', date],
    queryFn: () =>
      apiClient.get<DailyRow[]>(`/api/daily?from=${date}&to=${date}`, {
        schema: DailyRowsSchema,
      }),
    staleTime: STALE_TIME_DAILY,
  });

  const workoutsQuery = useQuery<WorkoutRow[]>({
    queryKey: ['workouts', date],
    queryFn: () =>
      apiClient.get<WorkoutRow[]>(`/api/workouts?from=${date}&to=${date}`, {
        schema: WorkoutRowsSchema,
      }),
    staleTime: STALE_TIME_DAILY,
  });

  const exerciseWeightsQuery = useQuery<ExerciseWeightRow[]>({
    queryKey: ['exercise-weights'],
    queryFn: () =>
      apiClient.get<ExerciseWeightRow[]>('/api/exercise-weights', {
        schema: ExerciseWeightRowsSchema,
      }),
    staleTime: STALE_TIME_SLOW,
  });

  // Pull actual-sets so the Day view can show session-time targets when
  // a session has been completed for this date. Without this, the Day
  // view shows the BUMPED target the engine moved to right after sync,
  // not the target the operator was working against during the session.
  // The same query is reused by the session inspector's comparison and the
  // TRENDS panels (TanStack dedupes by queryKey), so no extra round-trip.
  const actualSetsQuery = useQuery<ActualSetRow[]>({
    queryKey: ['actual-sets'],
    queryFn: () =>
      apiClient.get<ActualSetRow[]>('/api/speediance/actual-sets', {
        schema: ActualSetRowsSchema,
      }),
    staleTime: STALE_TIME_SLOW,
  });

  // Mutations — optimistic updates patterned exactly like the legacy today.tsx
  // (see U4 plan). Snapshot+rollback so transient API failures don't desync the
  // cache from the input field.
  const weightMut = useMutation<WeightRow, Error, { date: string; weight_kg: number }>({
    mutationFn: (body) =>
      apiClient.post<WeightRow>('/api/weights', body, { schema: WeightRowSchema }),
    onMutate: async (body) => {
      await qc.cancelQueries({ queryKey: ['weights'] });
      const prev = qc.getQueryData<WeightRow[]>(['weights']) ?? [];
      const next: WeightRow[] = [
        ...prev.filter((r) => r.date !== body.date),
        { date: body.date, weight_kg: body.weight_kg, updated_at: Date.now() },
      ].sort((a, b) => a.date.localeCompare(b.date));
      qc.setQueryData(['weights'], next);
      return { prev } as { prev: WeightRow[] };
    },
    onError: (_e, _v, ctx) => {
      const c = ctx as { prev: WeightRow[] } | undefined;
      if (c) qc.setQueryData(['weights'], c.prev);
    },
    onSettled: () => qc.invalidateQueries({ queryKey: ['weights'] }),
  });

  // Counterpart to weightMut for the Progress chart popover's DELETE action.
  // The optimistic update drops the row by date; rollback restores the prior
  // list on API failure. The endpoint is idempotent so retries are safe.
  const weightDeleteMut = useMutation<void, Error, { date: string }>({
    mutationFn: (body) =>
      apiClient.delete<void>(`/api/weights?date=${encodeURIComponent(body.date)}`),
    onMutate: async (body) => {
      await qc.cancelQueries({ queryKey: ['weights'] });
      const prev = qc.getQueryData<WeightRow[]>(['weights']) ?? [];
      const next = prev.filter((r) => r.date !== body.date);
      qc.setQueryData(['weights'], next);
      return { prev } as { prev: WeightRow[] };
    },
    onError: (_e, _v, ctx) => {
      const c = ctx as { prev: WeightRow[] } | undefined;
      if (c) qc.setQueryData(['weights'], c.prev);
    },
    onSettled: () => qc.invalidateQueries({ queryKey: ['weights'] }),
  });

  const dailyMut = useMutation<
    DailyRow,
    Error,
    {
      date: string;
      calories?: number;
      protein_g?: number;
      fat_g?: number;
      carb_g?: number;
      meals_eaten?: MealsEatenMap;
      correction?: boolean;
    }
  >({
    mutationFn: (body) =>
      apiClient.post<DailyRow>('/api/daily', body, { schema: DailyRowSchema }),
    onMutate: async (body) => {
      await qc.cancelQueries({ queryKey: ['daily', date] });
      const prev = qc.getQueryData<DailyRow[]>(['daily', date]) ?? [];
      const existing = prev.find((r) => r.date === body.date);
      // Field-level merge mirrors the server's partial-merge upsert: only
      // overwrite columns the caller actually sent; preserve the rest.
      const merged: DailyRow = {
        date: body.date,
        calories: body.calories !== undefined ? body.calories : (existing?.calories ?? null),
        protein_g: body.protein_g !== undefined ? body.protein_g : (existing?.protein_g ?? null),
        fat_g: body.fat_g !== undefined ? body.fat_g : (existing?.fat_g ?? null),
        carb_g: body.carb_g !== undefined ? body.carb_g : (existing?.carb_g ?? null),
        meals_eaten:
          body.meals_eaten !== undefined ? body.meals_eaten : (existing?.meals_eaten ?? null),
        updated_at: Date.now(),
      };
      const next = [...prev.filter((r) => r.date !== body.date), merged].sort((a, b) =>
        a.date.localeCompare(b.date),
      );
      qc.setQueryData(['daily', date], next);
      return { prev } as { prev: DailyRow[] };
    },
    onError: (_e, _v, ctx) => {
      const c = ctx as { prev: DailyRow[] } | undefined;
      if (c) qc.setQueryData(['daily', date], c.prev);
    },
    onSettled: () => {
      qc.invalidateQueries({ queryKey: ['daily', date] });
      qc.invalidateQueries({ queryKey: ['meal-items', date] });
    },
  });

  const workoutMut = useMutation<WorkoutRow, Error, WorkoutMutVars>({
    mutationFn: (body) =>
      apiClient.post<WorkoutRow>('/api/workouts', body, { schema: WorkoutRowSchema }),
    onMutate: async (body) => {
      await qc.cancelQueries({ queryKey: ['workouts', date] });
      const prev = qc.getQueryData<WorkoutRow[]>(['workouts', date]) ?? [];
      const existing = prev.find((r) => r.date === body.date && r.type === body.type);
      const mergedStatus = body.status ?? existing?.status ?? 'not_yet';
      const mergedExercisesDone =
        body.exercises_done !== undefined
          ? body.exercises_done
          : (existing?.exercises_done ?? null);
      const next: WorkoutRow[] = [
        ...prev.filter((r) => !(r.date === body.date && r.type === body.type)),
        {
          date: body.date,
          type: body.type,
          // A status/exercises toggle never changes who wrote the row; a
          // brand-new row from the operator is manual.
          source: existing?.source ?? 'manual',
          status: mergedStatus,
          completed: mergedStatus === 'completed' ? 1 : 0,
          exercises_done: mergedExercisesDone,
          // Preserve the existing coach prescription / strength session so an
          // optimistic done/exercise toggle on a coach day doesn't blank the
          // badge or exercise list until the refetch lands. A brand-new row
          // has neither.
          coach_prescription: existing?.coach_prescription ?? null,
          coach_strength_session: existing?.coach_strength_session ?? null,
          updated_at: Date.now(),
        },
      ];
      qc.setQueryData(['workouts', date], next);
      return { prev } as { prev: WorkoutRow[] };
    },
    onError: (_e, _v, ctx) => {
      const c = ctx as { prev: WorkoutRow[] } | undefined;
      if (c) qc.setQueryData(['workouts', date], c.prev);
    },
    onSettled: () => {
      qc.invalidateQueries({ queryKey: ['workouts', date] });
      qc.invalidateQueries({ queryKey: ['workouts'] });
      qc.invalidateQueries({ queryKey: ['data-health'] });
    },
  });

  const bodyweightActualMut = useMutation<
    ManualBodyweightActualsResponse,
    Error,
    ManualBodyweightActuals
  >({
    mutationFn: (body) => apiClient.post<ManualBodyweightActualsResponse>(
      '/api/speediance/actual-sets/manual',
      body,
      { schema: ManualBodyweightActualsResponseSchema },
    ),
    onSettled: () => {
      qc.invalidateQueries({ queryKey: ['actual-sets'] });
      qc.invalidateQueries({ queryKey: ['workouts', date] });
      qc.invalidateQueries({ queryKey: ['workouts'] });
      qc.invalidateQueries({ queryKey: ['data-health'] });
    },
  });

  // Slot override (U5 of operator-rails-and-override). Atomic on the
  // server: any non-skipped rows for the date are marked 'skipped' and a
  // fresh row at the chosen type is inserted at 'not_yet'. We invalidate
  // both the per-date workouts query (Day route) and the unscoped one
  // (used for streak / 4-week session counts) so every consumer re-reads.
  const workoutOverrideMut = useMutation<WorkoutRow, Error, WorkoutOverrideVars>({
    mutationFn: (body) =>
      apiClient.post<WorkoutRow>('/api/workouts/override', body, {
        schema: WorkoutRowSchema,
      }),
    onSettled: () => {
      qc.invalidateQueries({ queryKey: ['workouts', date] });
      qc.invalidateQueries({ queryKey: ['workouts'] });
    },
  });

  const exerciseWeightMut = useMutation<
    ExerciseWeightRow,
    Error,
    { exercise_key: string; weight_kg: number }
  >({
    mutationFn: (body) =>
      apiClient.put<ExerciseWeightRow>('/api/exercise-weights', body, {
        schema: ExerciseWeightRowSchema,
      }),
    onMutate: async (body) => {
      await qc.cancelQueries({ queryKey: ['exercise-weights'] });
      const prev = qc.getQueryData<ExerciseWeightRow[]>(['exercise-weights']) ?? [];
      const next: ExerciseWeightRow[] = [
        ...prev.filter((r) => r.exercise_key !== body.exercise_key),
        {
          exercise_key: body.exercise_key,
          weight_kg: body.weight_kg,
          updated_at: Date.now(),
          // Manual write clears the deload hint — optimistic mirror of
          // what the server's PUT response returns.
          deload_flag: false,
        },
      ].sort((a, b) => a.exercise_key.localeCompare(b.exercise_key));
      qc.setQueryData(['exercise-weights'], next);
      return { prev } as { prev: ExerciseWeightRow[] };
    },
    onError: (_e, _v, ctx) => {
      const c = ctx as { prev: ExerciseWeightRow[] } | undefined;
      if (c) qc.setQueryData(['exercise-weights'], c.prev);
    },
    onSettled: () => qc.invalidateQueries({ queryKey: ['exercise-weights'] }),
  });

  // Derived values
  const profile = profileQuery.data ?? DEFAULT_PROFILE;
  // Macro target effective on the active date (as-of history; falls back to
  // defaults before the first row). Day-tab macro tiles read THIS, not the
  // profile singleton, so a past date shows the target that applied then.
  const effectiveTarget = resolveEffectiveTarget(targetHistory, date);
  const weightLog = weightsQuery.data ?? [];
  // Apply the soft start_date filter at the boundary — every downstream
  // derived value (currentWeight, weeklyRate, streak inputs) operates on
  // the filtered list, so the rest of the file is agnostic to "before
  // program start" rows.
  const sortedWeights = filterFromStart(
    [...weightLog].sort((a, b) => a.date.localeCompare(b.date)),
    profile.start_date,
  );
  const todayRow = (dailyQuery.data ?? []).find((r) => r.date === date);
  // Active-plan resolution (U5 of operator-rails-and-override): a
  // workout_log row — operator override or coach-applied — always wins;
  // the weekday's standing commitment is the only fallback, and a date
  // with neither has no plan.
  const { todayPlan, todayWorkout } = deriveTodayPlanAndWorkout(
    workoutsQuery.data ?? [],
    date,
    profile.weekly_commitments,
  );
  const todayDone = todayWorkout?.status === 'completed';
  const exercisesDone: ExercisesDoneMap = todayWorkout?.exercises_done ?? {};
  const exerciseWeightsMap: Record<string, number> = Object.fromEntries(
    (exerciseWeightsQuery.data ?? []).map((r) => [r.exercise_key, r.weight_kg]),
  );
  // Parallel map of deload hints, keyed the same way. Kept as a separate
  // record so existing consumers using exerciseWeightsMap as
  // Record<string, number> don't have to switch to a richer object shape.
  const deloadFlagMap: Record<string, boolean> = Object.fromEntries(
    (exerciseWeightsQuery.data ?? []).map((r) => [r.exercise_key, r.deload_flag]),
  );
  // Per-exercise session-time target for THIS date. Key is present
  // whenever actual_set rows exist for the (exercise, date) pair,
  // regardless of whether a target was set at session time. Consumers
  // use KEY PRESENCE (Object.hasOwn) to decide "exercise is logged →
  // lock the cell", and the VALUE (number | null) for what to display.
  // Skipping null-target rows would lose the "logged" signal for any
  // exercise the operator logged without a working weight set — and
  // could let a post-hoc PUT retroactively appear as the historical
  // target.
  const sessionTimeTargetMap: Record<string, number | null> = {};
  for (const row of actualSetsQuery.data ?? []) {
    if (row.date !== date) continue;
    if (Object.hasOwn(sessionTimeTargetMap, row.exercise_key)) continue;
    sessionTimeTargetMap[row.exercise_key] = row.target_kg_at_session;
  }

  // Today's weight (logged today) and yesterday's weight (most recent prior).
  const weightTodayRow = sortedWeights.find((r) => r.date === date);
  const weightToday = weightTodayRow?.weight_kg ?? null;
  const priorWeights = sortedWeights.filter((r) => r.date < date);
  const weightYesterday =
    priorWeights.length > 0 ? priorWeights[priorWeights.length - 1]!.weight_kg : null;

  const currentWeight =
    weightToday ?? weightYesterday ?? profile.weight_start_kg ?? 0;

  const weeklyRate = computeWeeklyRate(sortedWeights);
  const phase = derivePhase(weeklyRate);
  const week = programWeek(profile);
  const toGoal = currentWeight - (profile.goal_weight_kg ?? 0);

  return {
    // raw queries (for error/loading checks)
    queries: {
      profile: profileQuery,
      weights: weightsQuery,
      daily: dailyQuery,
      workouts: workoutsQuery,
      exerciseWeights: exerciseWeightsQuery,
      actualSets: actualSetsQuery,
    },
    // mutations
    mutations: {
      weight: weightMut,
      weightDelete: weightDeleteMut,
      daily: dailyMut,
      workout: workoutMut,
      bodyweightActual: bodyweightActualMut,
      workoutOverride: workoutOverrideMut,
      exerciseWeight: exerciseWeightMut,
    },
    // derived
    profile,
    effectiveTarget,
    todayPlan,
    todayWorkout,
    todayDone,
    exercisesDone,
    todayRow,
    sortedWeights,
    weightToday,
    weightYesterday,
    currentWeight,
    weeklyRate,
    phase,
    week,
    toGoal,
    exerciseWeightsMap,
    deloadFlagMap,
    sessionTimeTargetMap,
    actualSetsForDate: (actualSetsQuery.data ?? []).filter((row) => row.date === date),
  };
}
